import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BottomSheet } from '@/components/BottomSheet/BottomSheet';
import Button from '@/components/SharedButton/Button';
import { useCurrency } from '@/context/CurrencyContext/CurrencyContext';
import styles from './CheckoutBottomSheetCustomTip.module.scss';

interface Props {
  onConfirm: (amount: number) => void;
  onClose: () => void;
}

const CheckoutBottomSheetCustomTip = ({ onConfirm, onClose }: Props) => {
  const { t } = useTranslation();
  const { currencySymbol } = useCurrency();
  const [amount, setAmount] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value;

    value = value.replace(',', '.');

    // Limit to maximum 10 characters (excluding the decimal point)
    const digitsOnly = value.replace('.', '');
    if (digitsOnly.length > 6) {
      return;
    }

    if (/^\d*(\.\d{0,2})?$/.test(value)) {
      setAmount(value);
    }
  };

  const handleAmountBlur = () => {
    if (amount === '' || isNaN(Number(amount))) {
      setAmount('');
      return;
    }

    setAmount(Number(amount).toFixed(2));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!amount || isNaN(Number(amount)) || Number(amount) === 0) return;
    onConfirm(Number(amount));
  };

  const handleButtonClick = () => {
    const form = inputRef.current?.form;
    if (form) {
      const event = new Event('submit', { cancelable: true, bubbles: true });
      form.dispatchEvent(event);
    }
  };

  return (
    <div>
      <BottomSheet closable={true} onClose={onClose} fromTheTop={true}>
        <div className={styles.infoContainer}>
          <h2>{t('checkout.customTip.customTip')}</h2>
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.inputWrapper}>
              <label>{currencySymbol}</label>
              <input
                type="number"
                inputMode="decimal"
                step="0.01"
                min="0"
                value={amount}
                className={styles.input}
                onChange={handleAmountChange}
                onBlur={handleAmountBlur}
                onWheel={e => e.currentTarget.blur()}
                ref={inputRef}
                placeholder="0.00"
              />
            </div>
            <div className={styles.confirmButton}>
              <div className={styles.buttonWrapper}>
                <Button
                  label={t('checkout.splitEqualMode.confirm')}
                  onClick={handleButtonClick}
                  disabled={!amount || isNaN(Number(amount)) || Number(amount) === 0}
                />
              </div>
            </div>
          </form>
        </div>
      </BottomSheet>
    </div>
  );
};

export default CheckoutBottomSheetCustomTip;
